import { action, makeObservable, observable } from "mobx";
import { GET_FILE_INFO_BY_ID } from "src/api/cred";
import { GetFileInfoById, GetFileInfoById_file, GetFileInfoByIdVariables } from "src/api/generated/GetFileInfoById";
import { RootStore } from "src/stores/RootStore";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { ISplitPdfFile } from "./InvoiceTypes";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { IPdfSplitManuallyRequestBody } from "src/network/NetworkTypes";
import { Route } from "src/config/routes";
import { MessageType } from "src/components/notifications/Notifier";
import i18next from "i18next";
import Axios from "src/network/Axios";

export enum STATE {
    INIT,
    LOADED,
    SPLITTING_PDF,
    ERROR_LOADING_FILE
}

export class SplitPdfStore {
    rootStore: RootStore;

    currentState: STATE = STATE.INIT;

    currentFile: ISplitPdfFile | undefined = undefined;

    numberOfPagesPerRow: number = 4;
    numberOfPages: number = 0;

    colWidth: number = 300;

    splitPoints: number[] = [];

    history: any = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            numberOfPagesPerRow: observable,
            numberOfPages: observable,
            currentState: observable,
            currentFile: observable,
            colWidth: observable,
            splitPoints: observable,
            history: observable,

            init: action,
            setNumberOfPagesPerRow: action,
            setCurrentState: action,
            setCurrentFile: action,
            setNumberOfPages: action,
            setColWidth: action,
            setSplitPoints: action,
            splitPdf: action,
            setHistory: action
        });
    }

    init = async (fileId: string) => {
        const currentFile: ISplitPdfFile = {};

        const downloadedFile = await this.downloadPdf(fileId);

        if (downloadedFile) {
            let fileBlob = downloadedFile.data;
            const objUrl = window.URL.createObjectURL(fileBlob);
            currentFile.url = objUrl;
            const pdfFileInfo = await this.getPdfInfo(fileId);

            if (pdfFileInfo) {
                currentFile.name = pdfFileInfo.name;
                currentFile.fileId = pdfFileInfo.id;
            }

            this.setCurrentFile(currentFile);
            this.setCurrentState(STATE.LOADED);
        } else {
            this.setCurrentState(STATE.ERROR_LOADING_FILE);
        }
    };

    setNumberOfPagesPerRow = (numberOfPagesPerRow: number) => {
        this.numberOfPagesPerRow = numberOfPagesPerRow;
    };

    setNumberOfPages = (numberOfPages: number) => {
        this.numberOfPages = numberOfPages;
    };

    setCurrentState = (currentState: STATE) => {
        this.currentState = currentState;
    };

    setCurrentFile = (file?: ISplitPdfFile) => {
        this.currentFile = file;
    };

    setColWidth = (colWidth: number) => {
        this.colWidth = colWidth;
    };

    setSplitPoints = (splitPoints: number[]) => {
        this.splitPoints = splitPoints;
    };

    setHistory(history: any) {
        this.history = history;
    }

    downloadPdf = async (fileId: string) => {
        const downloadedPdf = await this.rootStore.invoiceStore.downloadInvoiceFile(fileId);

        return downloadedPdf;
    };

    getPdfInfo = async (fileId: string): Promise<GetFileInfoById_file | undefined> => {
        let pdfFileInfo: GetFileInfoById_file | undefined = undefined;

        const { data }: { data: GetFileInfoById } = await apolloClientInstance.query<
            GetFileInfoById,
            GetFileInfoByIdVariables
        >({
            query: GET_FILE_INFO_BY_ID,
            variables: {
                fileid: fileId
            }
        });

        if (data.file.length > 0) {
            const fileInfo = data.file[0];
            pdfFileInfo = fileInfo;
        }

        return pdfFileInfo;
    };

    splitPdf = async () => {
        this.setCurrentState(STATE.SPLITTING_PDF);
        let successfullySplittedPdf = false;

        try {
            const splitPdfRequestBody: IPdfSplitManuallyRequestBody = {
                fileid: this.currentFile?.fileId || "",
                splits: this.splitPoints
            };

            await Axios.getAuthInstance().post(NetworkConfig.pdfSplitManuallyUrl, splitPdfRequestBody, {
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });

            successfullySplittedPdf = true;

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.pdf_split.split_success"),
                MessageType.SUCCESS
            );

            if (this.history) {
                this.history.replace(`${Route.manager}${Route.creditors}?tab=import`);
            }
        } catch (error) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.pdf_split.split_error"),
                MessageType.ERROR
            );
            console.error("Error splitting pdf: ", error);
        }

        this.setCurrentState(STATE.INIT);
        return successfullySplittedPdf;
    };
}
